import { ALGO_VERSION, formatGr, sortList, type Id, type StoreModel } from '@odhaczka/core';
import { AnimatePresence, motion, MotionConfig, useReducedMotion } from 'motion/react';
import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { AddItemsSheet } from '../components/AddItemsSheet';
import { CopiedRouteNotice } from '../components/shop/CopiedRouteNotice';
import { DoneRow } from '../components/shop/DoneRow';
import { FinishSheet } from '../components/shop/FinishSheet';
import { IconChevronDown, IconPlus } from '../components/shop/icons';
import { ItemSheet } from '../components/shop/ItemSheet';
import { ItemText } from '../components/shop/ItemText';
import { NearbyStoreCard } from '../components/shop/NearbyStoreCard';
import { ItemPriceInfo, rowPriceLabel } from '../components/shop/price';
import { ReorderList } from '../components/shop/ReorderList';
import { ShopHeader } from '../components/shop/ShopHeader';
import { Snackbar, type SnackbarMessage } from '../components/shop/Snackbar';
import { joinNames, plural, STATUS_LABEL } from '../components/shop/status';
import { SuggestionBanner } from '../components/shop/SuggestionBanner';
import { SwipeRow } from '../components/shop/SwipeRow';
import { useLiveNotices } from '../components/shop/useLiveNotices';
import { useProductResolver } from '../components/shop/useProductResolver';
import { useWakeLock } from '../components/shop/useWakeLock';
import { StorePicker } from '../components/StorePicker';
import { primaryBtn, secondaryBtn } from '../components/ui/classes';
import { SkeletonRows } from '../components/ui/Skeleton';
import { memberFirstName, memberInitial, useMembers } from '../data/household';
import { editItem, removeItem, restoreItem, setListStore, useList, useListItems } from '../data/lists';
import type { DraftItem, Resolution } from '../data/products';
import { listEstimate, usePriceIndex } from '../data/prices';
import { promoShort, promosForItem, usePromoIndex } from '../data/promotions';
import { useListShoppers } from '../data/presence';
import { useListLines } from '../data/receipts';
import {
  acceptSuggestion,
  checkItem,
  finishShopping,
  manualMove,
  markRemainingMissing,
  rejectSuggestion,
  reopenList,
  setItemNote,
  suggestSubstitute,
  uncheckItem,
  undoCheck,
  undoUncheck,
  useActiveVisit,
  useStoreModel,
  type CheckExtra,
  type CheckStatus,
  type UndoToken,
} from '../data/shopping';
import { autoSeed, confirmedCount, isCopiedRoute } from '../data/routeAssist';
import { isLidl, resetStoreOrder, useStore } from '../data/stores';
import type { ListItemRow } from '../db/types';
import { currentUserId } from '../lib/auth';
import { setViewingList, useLiveConnected } from '../sync/live';

// Lista bez sklepu: wszystko „po drodze”, w kolejności z listy.
const EMPTY_MODEL: StoreModel = { algoVersion: ALGO_VERSION, storeId: '', eventCount: 0, lastHlc: null, byProduct: {} };

type Entry =
  | { key: string; kind: 'label'; text: string; hint?: string | undefined }
  | { key: string; kind: 'row'; item: ListItemRow; guessed?: boolean };

/** Tryb sklepu: lista w kolejności trasy, odhaczanie jedną ręką. */
export function ShopPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const list = useList(id);
  const items = useListItems(id);
  const storeId = list?.storeId ?? null;
  const store = useStore(storeId);
  const model = useStoreModel(storeId);
  const resolve = useProductResolver();
  const prices = usePriceIndex();
  const promos = usePromoIndex();
  const paidLines = useListLines(id);
  const reduceMotion = useReducedMotion();

  const [sheet, setSheet] = useState<{ id: Id; open: boolean } | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [reordering, setReordering] = useState(false);
  // null = domyślnie: zwinięte w trakcie zakupów, rozwinięte po zakończeniu.
  const [showDone, setShowDone] = useState<boolean | null>(null);
  const [finishAsk, setFinishAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [snack, setSnack] = useState<SnackbarMessage | null>(null);
  // Wieści od domowników — u góry, żeby nie zasłaniały „Cofnij” na dole.
  const [notice, setNotice] = useState<SnackbarMessage | null>(null);
  const [highlight, setHighlight] = useState<{ id: Id; at: number } | null>(null);
  const snackId = useRef(0);
  const [params, setParams] = useSearchParams();

  const me = currentUserId();
  const members = useMembers();
  const nameOf = useCallback((userId: Id) => memberFirstName(members.get(userId)), [members]);
  const shoppers = useListShoppers(id);
  const live = useLiveConnected();
  const myVisit = useActiveVisit(id);
  const [openedAt] = useState(() => Date.now());
  // „Nowa” = dopisana przez kogoś po starcie mojej wizyty (a bez wizyty — po otwarciu listy).
  const since = myVisit?.startedMs ?? openedAt;

  // Propozycje zamienników od innych — nie mogą zginąć w zwiniętej sekcji odhaczonych.
  const suggested = (items ?? []).filter((i) => i.suggestion && i.suggestedBy !== me);
  const suggestedKey = suggested.map((i) => `${i.id}:${i.suggestion}`).join('|');
  const [seenSuggestions, setSeenSuggestions] = useState(suggestedKey);
  if (suggestedKey !== seenSuggestions) {
    setSeenSuggestions(suggestedKey);
    // Nowa propozycja przywraca domyślne zachowanie sekcji (rozwiniętej, póki są propozycje).
    if (suggestedKey) setShowDone(null);
  }

  useWakeLock(list?.status === 'active');

  // Nowy sklep sieci: trasa innego sklepu tej sieci od razu, także offline.
  const shopping = list?.status === 'active';
  useEffect(() => {
    if (storeId && shopping) void autoSeed(storeId);
  }, [storeId, shopping]);

  // Serwer nie wysyła powiadomień o liście, którą mam właśnie otwartą.
  useEffect(() => {
    if (!id) return;
    setViewingList(id);
    return () => setViewingList(null);
  }, [id]);

  useEffect(() => {
    if (!highlight) return;
    const t = window.setTimeout(() => setHighlight(null), 2000);
    return () => window.clearTimeout(t);
  }, [highlight]);

  const sorted = useMemo(() => {
    if (!items || !resolve) return null;
    const rows = items.map((i) => ({
      itemId: i.id,
      productId: i.productId,
      listIndex: i.position,
      // Źródłem prawdy jest status (pola synchronizują się osobno).
      checkedAt: i.status === 'pending' ? null : (i.checkedMs ?? 0),
      item: i,
    }));
    return sortList(model ?? EMPTY_MODEL, rows, { resolveProduct: resolve });
  }, [items, model, resolve]);

  /** Przewija do pozycji, podświetla ją i (domyślnie) otwiera jej arkusz — link z powiadomienia, „Pokaż”. */
  const focusItem = (itemId: Id, withSheet = true) => {
    const item = items?.find((i) => i.id === itemId);
    if (!item) return;
    setReordering(false);
    if (item.status !== 'pending') setShowDone(true);
    setHighlight({ id: itemId, at: Date.now() });
    // Po wyrenderowaniu (sekcja odhaczonych mogła się dopiero rozwinąć): pozycja w górnej części ekranu, nad arkuszem.
    window.setTimeout(() => {
      const el = document.querySelector(`[data-item-id="${CSS.escape(itemId)}"]`);
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 140, behavior: reduceMotion ? 'auto' : 'smooth' });
    }, 60);
    // Arkusz chwilę później — najpierw widać, o którą pozycję chodzi (na krótkiej liście arkusz by ją zasłonił).
    // Zakończona lista jest tylko do odczytu: samo podświetlenie.
    if (withSheet && list?.status === 'active') window.setTimeout(() => setSheet({ id: itemId, open: true }), 700);
  };

  useLiveNotices(items, { listId: id, me, nameOf, since }, (n) => {
    if (n.vibrate) navigator.vibrate?.(n.vibrate);
    const itemId = n.itemId;
    setNotice({ id: ++snackId.current, text: n.text, action: itemId ? { label: 'Pokaż', onClick: () => focusItem(itemId) } : undefined });
  });

  const ready = list !== undefined && items !== undefined && sorted !== null && !(storeId !== null && model === undefined);
  // Link z powiadomienia: /lists/:id?item=… — obsługiwany raz, po wczytaniu; parametr znika z adresu.
  const deepItem = params.get('item');
  const openDeepLink = useEffectEvent((itemId: Id) => {
    focusItem(itemId);
    setParams(
      (p) => {
        p.delete('item');
        return p;
      },
      { replace: true },
    );
  });
  useEffect(() => {
    if (deepItem && ready) openDeepLink(deepItem);
  }, [deepItem, ready]);

  if (!ready) return <ShopSkeleton />;
  if (list === null || list.deleted || !id) return <NotFound />;

  const listId = id;
  const pending = [...sorted.unknown, ...sorted.known];
  const total = items.length;
  const readOnly = list.status !== 'active';
  const canReorder = !readOnly && storeId !== null && pending.some((r) => r.productId !== null);
  const reorderOn = reordering && canReorder;
  const doneOpen = showDone ?? (readOnly || sorted.done.some((r) => suggested.includes(r.item)));
  // Pierwsza propozycja w kolejności ekranu: najpierw do kupienia, potem odhaczone.
  const firstSuggested = [...pending, ...sorted.done].find((r) => suggested.includes(r.item));
  // Trasa tylko z kopii innego sklepu (i zgadywanek): zamiast wskazówki pierwszej wizyty komunikat o kopii.
  const copied = !!model && isCopiedRoute(model);
  const firstVisit = model ? confirmedCount(model) === 0 && !copied : false;
  // Ręczne przestawienie po kopii = użytkownik już poprawia trasę; „Cofnij” (reset) skasowałoby poprawki.
  const copiedFrom = copied && !Object.values(model.byProduct).some((p) => p.manual) ? model.seededFrom : undefined;
  const isGuessed = (i: ListItemRow) => !!(i.productId && resolve && model?.byProduct[resolve(i.productId)]?.guessed);

  const toast = (text: string, action?: SnackbarMessage['action']) => setSnack({ id: ++snackId.current, text, action });

  const undo = (token: UndoToken) => void undoCheck(token).catch(() => toast('Nie udało się cofnąć'));

  const check = async (item: ListItemRow, status: CheckStatus, extra?: CheckExtra): Promise<boolean> => {
    try {
      const token = await checkItem(item.id, status, extra);
      toast(`„${item.name}” → ${STATUS_LABEL[status]}`, { label: 'Cofnij', onClick: () => undo(token) });
      return true;
    } catch {
      toast('Nie udało się zapisać');
      return false;
    }
  };

  const uncheck = async (item: ListItemRow) => {
    try {
      const token = await uncheckItem(item.id);
      toast(`„${item.name}” → ${STATUS_LABEL.pending}`, {
        label: 'Cofnij',
        onClick: () => void undoUncheck(token).catch(() => toast('Nie udało się cofnąć')),
      });
    } catch {
      toast('Nie udało się zapisać');
    }
  };

  const accept = async (item: ListItemRow) => {
    try {
      const token = await acceptSuggestion(item.id);
      toast(`„${item.name}” → zamiennik: ${item.suggestion}`, { label: 'Cofnij', onClick: () => undo(token) });
    } catch {
      toast('Ta propozycja jest już nieaktualna');
    }
  };

  const reject = async (item: ListItemRow, mine: boolean) => {
    try {
      await rejectSuggestion(item.id);
      toast(mine ? 'Propozycja wycofana' : 'Propozycja odrzucona');
    } catch {
      toast('Nie udało się zapisać');
    }
  };

  const suggest = async (item: ListItemRow, text: string | null) => {
    try {
      await suggestSubstitute(item.id, text);
      toast(text ? 'Propozycja wysłana' : 'Propozycja wycofana');
    } catch {
      toast('Nie udało się zapisać');
    }
  };

  const edit = async (item: ListItemRow, next: DraftItem, resolution?: Resolution) => {
    try {
      await editItem(item.id, next, resolution);
    } catch {
      toast('Nie udało się zapisać');
    }
  };

  const remove = async (item: ListItemRow) => {
    try {
      await removeItem(item.id);
      toast(`Usunięto „${item.name}”`, {
        label: 'Cofnij',
        onClick: () => void restoreItem(item.id).catch(() => toast('Nie udało się cofnąć')),
      });
    } catch {
      toast('Nie udało się usunąć');
    }
  };

  const finish = async (markMissing: boolean) => {
    setBusy(true);
    try {
      // Masowo, bez zdarzeń nauki — przy tych półkach nikt nie był.
      if (markMissing) await markRemainingMissing(listId);
      await finishShopping(listId);
      navigate(`/lists/${listId}/after`);
    } catch {
      setBusy(false);
      toast('Nie udało się zakończyć zakupów');
    }
  };

  const openSheet = (item: ListItemRow) => setSheet({ id: item.id, open: true });
  const closeSheet = () => setSheet((s) => (s ? { ...s, open: false } : s));
  const sheetItem = sheet ? (items.find((i) => i.id === sheet.id) ?? null) : null;
  const act = (fn: (item: ListItemRow) => void) => {
    closeSheet();
    if (sheetItem) fn(sheetItem);
  };

  const isFresh = (i: ListItemRow) => i.status === 'pending' && !!i.addedBy && i.addedBy !== me && (i.addedMs ?? 0) > since;
  const doneBy = (i: ListItemRow) =>
    i.checkedBy && i.checkedBy !== me ? { name: nameOf(i.checkedBy), initial: memberInitial(members.get(i.checkedBy)) } : null;
  const bannerFor = (i: ListItemRow) => {
    if (!i.suggestion) return null;
    const mine = !!me && i.suggestedBy === me;
    return (
      <SuggestionBanner
        by={mine ? null : i.suggestedBy ? nameOf(i.suggestedBy) : 'Ktoś'}
        text={i.suggestion}
        onAccept={readOnly ? undefined : () => void accept(i)}
        onReject={readOnly ? undefined : () => void reject(i, mine)}
      />
    );
  };
  const glow = (itemId: Id) =>
    highlight?.id === itemId && (
      <div aria-hidden className="pointer-events-none absolute inset-0 z-10 bg-amber-300/30 ring-2 ring-amber-400 ring-inset motion-safe:animate-pulse" />
    );

  // Obecność: kto jeszcze ma otwartą wizytę na tej liście.
  const others = (shoppers ?? []).filter((s) => !s.isMe);
  let presence: string | null = null;
  if (others.length > 0 && !readOnly) {
    const names = joinNames(others.map((s) => s.name));
    const many = others.length > 1;
    if ((shoppers ?? []).some((s) => s.isMe)) {
      const ids = new Set(others.map((s) => s.userId));
      const n = items.filter((i) => i.status !== 'pending' && !!i.checkedBy && ids.has(i.checkedBy)).length;
      presence = `${names} ${many ? 'też robią' : 'też robi'} te zakupy · ${n} ${plural(n, 'odhaczona', 'odhaczone', 'odhaczonych')}`;
    } else {
      const where = others[0]!.storeName ? ` w ${others[0]!.storeName}` : '';
      presence = `${names} ${many ? 'robią' : 'robi'} zakupy${where} — podgląd na żywo`;
    }
  }

  const entries: Entry[] = [];
  if (sorted.unknown.length > 0) {
    entries.push({
      key: 'label-unknown',
      kind: 'label',
      text: storeId ? 'Gdzieś po drodze' : 'Do kupienia',
      hint: storeId && firstVisit && !readOnly ? 'Odhaczaj w kolejności zbierania — zapamiętam, gdzie co leży' : undefined,
    });
    for (const r of sorted.unknown) entries.push({ key: r.itemId, kind: 'row', item: r.item });
  }
  if (sorted.known.length > 0) {
    if (sorted.unknown.length > 0) entries.push({ key: 'label-route', kind: 'label', text: 'Trasa' });
    for (const r of sorted.known) entries.push({ key: r.itemId, kind: 'row', item: r.item, guessed: isGuessed(r.item) });
  }

  const priceOf = (i: ListItemRow) => rowPriceLabel(i, prices, paidLines?.get(i.id));
  // Promocja tylko przy pozycjach do kupienia (po zakupach już nieistotna).
  const promoOf = (i: ListItemRow) => {
    if (i.status !== 'pending' || readOnly) return null;
    const p = promosForItem(i, prices, promos)[0];
    return p ? promoShort(p) : null;
  };
  const est = prices ? listEstimate(items, prices) : null;
  const estimate =
    est && est.priced > 0
      ? {
          text: `≈ ${formatGr(Math.round(est.totalGr / 100) * 100).replace(',00', '')}`,
          title: `Szacunek z ostatnich cen w Lidlu: ${est.priced} z ${est.count} pozycji ma cenę`,
        }
      : null;

  const rowTransition = reduceMotion ? { duration: 0 } : { duration: 0.18, ease: 'easeOut' as const };
  const allChecked = total > 0 && pending.length === 0;

  return (
    <MotionConfig reducedMotion="user">
      <ShopHeader
        title={list.title ?? 'Zakupy'}
        storeName={storeId ? (store?.name ?? '…') : null}
        done={sorted.done.length}
        total={total}
        onPickStore={() => setPickerOpen(true)}
        onToggleReorder={canReorder ? () => setReordering((v) => !v) : undefined}
        reordering={reorderOn}
        live={live}
        presence={presence}
        suggestions={!readOnly && firstSuggested ? { count: suggested.length, onShow: () => focusItem(firstSuggested.itemId, false) } : null}
        estimate={estimate}
      />

      <main className="flex-1 pb-4">
        {readOnly && (
          <div className="m-4 flex items-center gap-3 rounded-2xl bg-stone-200/70 p-4 dark:bg-stone-800/70">
            <div className="min-w-0 flex-1">
              <p className="font-semibold">{list.status === 'archived' ? 'Lista w archiwum' : 'Zakupy zakończone'}</p>
              <Link to={`/lists/${listId}/after`} className="text-sm text-brand-700 underline underline-offset-2 dark:text-brand-500">
                Zobacz podsumowanie
              </Link>
            </div>
            <button
              type="button"
              onClick={() => void reopenList(listId)}
              className="min-h-11 shrink-0 rounded-xl bg-white px-4 font-medium shadow-sm active:bg-stone-100 dark:bg-stone-900 dark:active:bg-stone-800"
            >
              Wznów
            </button>
          </div>
        )}

        {!readOnly && <NearbyStoreCard listId={listId} storeId={storeId} items={items} />}

        {!readOnly && storeId === null && (
          <div className="m-4 rounded-2xl border border-brand-100 bg-brand-50 p-4 dark:border-brand-800/50 dark:bg-brand-800/20">
            <p className="font-semibold">W którym sklepie jesteś?</p>
            <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">Wybierz sklep, a lista ułoży się w kolejności alejek.</p>
            <button type="button" onClick={() => setPickerOpen(true)} className={`${primaryBtn} mt-3`}>
              Wybierz sklep
            </button>
          </div>
        )}

        {!readOnly && storeId && copiedFrom && (
          <CopiedRouteNotice
            fromStoreId={copiedFrom}
            // Cofnięcie tylko po kopii automatycznej — przed nią trasa była pusta, więc reset niczego nie traci.
            onUndo={model?.seededAuto ? () => void resetStoreOrder(storeId).catch(() => toast('Nie udało się cofnąć')) : undefined}
          />
        )}

        {total === 0 && <p className="p-8 text-center text-stone-500">Ta lista jest pusta.</p>}

        {reorderOn ? (
          <>
            <p className="px-4 pt-3 text-sm text-stone-600 dark:text-stone-400">
              Przeciągnij za uchwyt na miejsce, w którym produkt leży w tym sklepie.
            </p>
            <ReorderList
              unknown={sorted.unknown.map((r) => r.item)}
              route={sorted.known.map((r) => r.item)}
              onMove={(productId, afterId, beforeId) => {
                if (storeId) void manualMove(storeId, productId, afterId, beforeId).catch(() => toast('Nie udało się zapisać'));
              }}
            />
          </>
        ) : (
          <ul>
            <AnimatePresence initial={false}>
              {entries.map((e) => (
                <motion.li
                  key={e.key}
                  layout="position"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={rowTransition}
                  className="relative overflow-hidden"
                  data-item-id={e.kind === 'row' ? e.item.id : undefined}
                >
                  {e.kind === 'row' && glow(e.item.id)}
                  {e.kind === 'label' ? (
                    <div className="px-4 pt-4 pb-1.5">
                      <h2 className="text-sm font-semibold tracking-wide text-stone-500 uppercase">{e.text}</h2>
                      {e.hint && <p className="mt-0.5 text-sm text-stone-500">{e.hint}</p>}
                    </div>
                  ) : readOnly ? (
                    <div className="flex min-h-14 items-center border-b border-stone-200 bg-white px-4 dark:border-stone-800 dark:bg-stone-900">
                      <ItemText item={e.item} guessed={e.guessed} price={priceOf(e.item)} promo={promoOf(e.item)}>
                        {bannerFor(e.item)}
                      </ItemText>
                    </div>
                  ) : (
                    <SwipeRow
                      item={e.item}
                      onCommit={(status) => check(e.item, status)}
                      onOpen={() => openSheet(e.item)}
                      fresh={isFresh(e.item)}
                      guessed={e.guessed}
                      banner={bannerFor(e.item)}
                      price={priceOf(e.item)}
                      promo={promoOf(e.item)}
                    />
                  )}
                </motion.li>
              ))}
            </AnimatePresence>
          </ul>
        )}

        {!reorderOn && sorted.done.length > 0 && (
          <section className="mt-4">
            <button
              type="button"
              onClick={() => setShowDone(!doneOpen)}
              aria-expanded={doneOpen}
              className="flex min-h-12 w-full items-center justify-between px-4 text-sm font-semibold tracking-wide text-stone-500 uppercase active:bg-stone-100 dark:active:bg-stone-900"
            >
              W koszyku i braki ({sorted.done.length})
              <IconChevronDown className={`size-5 transition-transform ${doneOpen ? 'rotate-180' : ''}`} />
            </button>
            {doneOpen && (
              <ul className="border-t border-stone-200 dark:border-stone-800">
                {sorted.done.map((r) => (
                  <li key={r.itemId} className="relative" data-item-id={r.itemId}>
                    {glow(r.itemId)}
                    <DoneRow
                      item={r.item}
                      onOpen={readOnly ? undefined : () => openSheet(r.item)}
                      by={doneBy(r.item)}
                      banner={bannerFor(r.item)}
                      price={priceOf(r.item)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </main>

      <div className="safe-bottom sticky bottom-0 z-20 border-t border-stone-200 bg-white/95 px-4 pt-3 backdrop-blur dark:border-stone-800 dark:bg-stone-900/95">
        {reorderOn ? (
          <button type="button" onClick={() => setReordering(false)} className={primaryBtn}>
            Gotowe
          </button>
        ) : readOnly ? (
          <Link to={`/lists/${listId}/after`} className={primaryBtn}>
            Podsumowanie
          </Link>
        ) : (
          <div className="flex flex-col gap-2">
            {allChecked && (
              <motion.p initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="text-center text-sm font-medium text-brand-700 dark:text-brand-500">
                Wszystko odhaczone ✓
              </motion.p>
            )}
            <div className="flex gap-2">
              <button type="button" onClick={() => setAdding(true)} className={`${secondaryBtn} w-auto! shrink-0 gap-1.5`}>
                <IconPlus /> Dopisz
              </button>
              {allChecked ? (
                <button type="button" disabled={busy} onClick={() => void finish(false)} className={`${primaryBtn} shadow-md shadow-brand-700/30`}>
                  Zakończ zakupy
                </button>
              ) : (
                <button type="button" disabled={busy || total === 0} onClick={() => setFinishAsk(true)} className={secondaryBtn}>
                  Zakończ zakupy
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <ItemSheet
        item={sheetItem}
        open={!!sheet?.open && sheetItem !== null}
        onClose={closeSheet}
        onCheck={(status, extra) => act((item) => void check(item, status, extra))}
        onUncheck={() => act((item) => void uncheck(item))}
        onNote={(note) => act((item) => void setItemNote(item.id, note).catch(() => toast('Nie udało się zapisać')))}
        onEdit={(next, resolution) => act((item) => void edit(item, next, resolution))}
        onRemove={() => act((item) => void remove(item))}
        onReorder={
          canReorder && sheetItem?.productId && sheetItem.status === 'pending'
            ? () => act(() => setReordering(true))
            : undefined
        }
        guessed={!!sheetItem && isGuessed(sheetItem)}
        onSuggest={(text) => act((item) => void suggest(item, text))}
        onAcceptSuggestion={() => act((item) => void accept(item))}
        onRejectSuggestion={() => act((item) => void reject(item, !!me && item.suggestedBy === me))}
        me={me}
        nameOf={nameOf}
        price={
          sheetItem ? (
            <ItemPriceInfo
              item={sheetItem}
              index={prices}
              lines={paidLines?.get(sheetItem.id)}
              promos={promosForItem(sheetItem, prices, promos)}
              allowPin={!storeId || (!!store && isLidl(store))}
            />
          ) : null
        }
      />

      <AddItemsSheet listId={listId} open={adding} onClose={() => setAdding(false)} />

      <FinishSheet open={finishAsk} pending={pending.length} busy={busy} onClose={() => setFinishAsk(false)} onFinish={(m) => void finish(m)} />

      <StorePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(sid) => void setListStore(listId, sid).catch(() => toast('Nie udało się zapisać'))}
        title={storeId ? 'Zmień sklep' : 'W którym sklepie jesteś?'}
        selectedId={storeId}
      />

      <Snackbar message={snack} onDismiss={() => setSnack(null)} offset={`calc(env(safe-area-inset-bottom, 0px) + ${allChecked ? 8.5 : 6}rem)`} />
      <Snackbar
        message={notice}
        onDismiss={() => setNotice(null)}
        placement="top"
        duration={4000}
        offset={presence ? 'calc(env(safe-area-inset-top, 0px) + 6.25rem)' : undefined}
      />
    </MotionConfig>
  );
}

function ShopSkeleton() {
  return (
    <div className="p-4">
      <div className="mb-5 h-10 w-2/3 animate-pulse rounded-lg bg-stone-200/70 dark:bg-stone-800/70" />
      <SkeletonRows count={6} />
    </div>
  );
}

function NotFound() {
  return (
    <div className="flex flex-col items-center gap-4 p-8 text-center">
      <p className="text-stone-600 dark:text-stone-400">Nie ma takiej listy — mogła zostać usunięta.</p>
      <Link to="/" className={primaryBtn}>
        Wróć do list
      </Link>
    </div>
  );
}
